import {
  Button,
  FeedCard,
  Icon,
  Toast,
  TooltipContent,
  TooltipRoot,
  TooltipTrigger,
} from '@socialchorus/shared-ui-components';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useSWRConfig } from 'swr';
import { usePatronSelector } from '../../../../common/use-patron-selector';
import {
  contentOperations,
  contentSelectors,
} from '../../../../models/content';
import {
  trackContentCardLike,
  trackContentCardUnlike,
} from '../../../../models/content/analytics';
import { getContentLikesDataKey } from '../../../../common/use-content-likes';
import { FeedCardActionsProps } from './type';
import { programSelectors } from '../../../../models/program';
import useFeatureFlag from '../../../../common/use-feature-flag';
import { Feature } from '../../../../models/features/features';
import { useContentExpandedReactions } from '../../../../common/use-content-expanded-reactions';

function FeedCardActionLikeOnly({
  content,
  analyticsData,
}: FeedCardActionsProps) {
  //controls minor layout changes associated with the reaction types deployment.
  const expandedReactionTypesDeployed = useFeatureFlag(
    Feature.EXPANDED_REACTION_TYPES_DEPLOY
  );

  const isLiked = usePatronSelector((s) =>
    contentSelectors.getContentLiked(s, { contentId: content.id })
  );
  const likeCount = usePatronSelector((s) =>
    contentSelectors.getContentLikeCount(s, { contentId: content.id })
  );
  const dispatch = useDispatch();
  const { mutate } = useSWRConfig();
  const { t } = useTranslation();

  const likeContent = (id: number) =>
    dispatch(contentOperations.likeContent(id));
  const unlikeContent = (id: number) =>
    dispatch(contentOperations.unlikeContent(id));

  const handleClick = () => {
    if (isLiked) {
      trackContentCardUnlike(content.id, analyticsData);
      unlikeContent(content.id);
    } else {
      trackContentCardLike(content.id, analyticsData);
      likeContent(content.id);
    }
    // Trigger revalidation on the content likes list
    void mutate(getContentLikesDataKey(content.id));
  };

  const contentTitle = usePatronSelector((s) =>
    contentSelectors.getContentFunctionalTitle(s, { contentId: content.id })
  );

  const likeActionKey = isLiked
    ? 'content.like_remove_action'
    : 'content.like_action';
  const actionLabel = t(likeActionKey, { title: contentTitle });

  return (
    <TooltipRoot>
      <TooltipTrigger asChild>
        <Button
          aria-label={actionLabel}
          label={
            expandedReactionTypesDeployed
              ? t('content.like', { count: 1 })
              : String(likeCount)
          }
          leftIcon={<Icon size={20}>thumb_up</Icon>}
          size="compact"
          variant="text"
          selected={isLiked}
          onClick={handleClick}
        />
      </TooltipTrigger>
      <TooltipContent
        description={
          isLiked ? t('content.like_remove') : t('content.like', { count: 1 })
        }
      />
    </TooltipRoot>
  );
}

export function FeedCardActionReact({
  contentId,
  analyticsData,
  size = 'compact',
  container,
}: {
  contentId: number;
  analyticsData: IAnalyticsData;
  size?: 'compact' | 'default';
  container?: HTMLElement | null;
}) {
  const { t } = useTranslation();

  const { selectedReaction, availableReactions, setNewReaction, isError } =
    useContentExpandedReactions({
      contentId: contentId,
      analyticsData,
    });

  if (!availableReactions?.length) {
    return null;
  }

  return (
    <>
      <FeedCard.ReactionButton
        selectedReactionId={selectedReaction?.id || null}
        availableReactions={availableReactions}
        onReactionSelect={setNewReaction}
        size={size}
        portalProps={{ container }}
      />
      {isError && (
        <Toast
          title={t('content.reaction.error')}
          variant="error"
          defaultOpen
        />
      )}
    </>
  );
}

export const FeedCardActionLikeButton = (props: FeedCardActionsProps) => {
  const expandedReactionTypesEnabled = usePatronSelector((state) =>
    programSelectors.getExpandedReactionTypesEnabled(state)
  );
  return expandedReactionTypesEnabled ? (
    <FeedCardActionReact
      contentId={props.content.id}
      analyticsData={props.analyticsData}
    />
  ) : (
    <FeedCardActionLikeOnly {...props} />
  );
};
